/**
 * VaultService - Manages vaults in the KnowledgeBase
 *
 * Vaults are like Obsidian vaults - separate workspaces for organizing notes.
 * Each vault has its own notes subcollection, folder metadata, and settings.
 *
 * Firestore structure:
 *   knowledgebase/{vaultId}           - Vault document
 *   knowledgebase/{vaultId}/notes/{noteId} - Notes subcollection
 */

import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  limit,
  FieldPath
} from 'firebase/firestore'
import { db, auth } from '@/services/firebase/config'
import { getIdTokenResult, onIdTokenChanged } from 'firebase/auth'
import { slugify } from './LinkParser.js'
import { createVault, VAULT_DEFAULTS } from '../types/vault.js'

const COLLECTION = 'knowledgebase'
const SHARE_CACHE_TTL_MS = 2 * 60 * 1000
const CAMPAIGN_CACHE_TTL_MS = 60 * 1000
const SHARE_SCAN_LIMIT = 50
const IN_QUERY_LIMIT = 10
const _shareLookupCache = new Map()
const _campaignAccessCache = new Map()

const chunkArray = (list, size = IN_QUERY_LIMIT) => {
  const safe = Array.isArray(list) ? list.filter(Boolean) : []
  if (!safe.length) return []
  const chunks = []
  for (let i = 0; i < safe.length; i += size) {
    chunks.push(safe.slice(i, i + size))
  }
  return chunks
}

const getVaultOwnerId = (data) => {
  const ownerId = data?.ownerId || data?.owner || null
  return ownerId ? String(ownerId) : null
}

const getVaultAccess = (data) => String(data?.access || 'PRIVATE').toUpperCase()

const getVaultAllowedUsers = (data) => {
  const top = Array.isArray(data?.allowedUserIds) ? data.allowedUserIds : []
  const fm = Array.isArray(data?.frontmatter?.allowedUserIds) ? data.frontmatter.allowedUserIds : []
  return Array.from(new Set([...top, ...fm].filter(Boolean).map((value) => String(value))))
}

const vaultMatchesSlug = (data, slug) => {
  if (!data || !slug) return false
  const docSlug = String(data.slug || '').trim()
  if (docSlug && docSlug === slug) return true
  const nameSlug = data?.name ? slugify(String(data.name)) : ''
  return !!nameSlug && nameSlug === slug
}

const canViewerReadVault = (data, viewerUid, accessibleCampaignIds = new Set()) => {
  const access = getVaultAccess(data)
  if (access === 'PUBLIC') return true
  if (!viewerUid) return false
  const ownerId = getVaultOwnerId(data)
  if (ownerId && ownerId === viewerUid) return true
  if ((access === 'CAMPAIGN' || access === 'CAMPAIGN_EDIT') && data?.campaignId) {
    return accessibleCampaignIds.has(String(data.campaignId))
  }
  if (access === 'USER_SPECIFIC') {
    return getVaultAllowedUsers(data).includes(String(viewerUid))
  }
  return false
}

let _lastAuthUid = null
try {
  _lastAuthUid = auth?.currentUser?.uid || null
  if (auth && typeof onIdTokenChanged === 'function') {
    onIdTokenChanged(auth, (user) => {
      const nextUid = user?.uid || null
      if (nextUid !== _lastAuthUid) {
        try {
          _shareLookupCache.clear()
          _campaignAccessCache.clear()
        } catch {}
        _lastAuthUid = nextUid
      }
    })
  }
} catch {}

/**
 * Normalize vault data from Firestore document
 */
function normalizeVault(docSnap) {
  if (!docSnap?.exists()) return null
  const data = docSnap.data()
  return {
    id: docSnap.id,
    ...createVault(data),
    createdAt: data.createdAt?.toDate?.() || data.createdAt || null,
    updatedAt: data.updatedAt?.toDate?.() || data.updatedAt || null
  }
}

export class VaultService {
  static _isDebug() {
    try {
      if (typeof window !== 'undefined') {
        const qs = String(window.location?.search || '').toLowerCase()
        if (qs.includes('kbdebug=1') || qs.includes('kbdebug=true') || qs.includes('debugkb=1')) {
          return true
        }
        const ls = window.localStorage?.getItem('kb-debug-kb')
        if (ls && ls.toString().toLowerCase() === 'true') return true
      }
    } catch {}
    try {
      // Fallback to dev env
      // eslint-disable-next-line no-undef
      if (typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') return true
    } catch {}
    return false
  }

  static _log(...args) {
    if (!VaultService._isDebug()) return
    try {
      // eslint-disable-next-line no-console
      console.debug('[VaultService:share]', ...args)
    } catch {}
  }

  static _readShareLookupCache(cacheKey) {
    const cached = _shareLookupCache.get(cacheKey)
    if (!cached) return undefined
    if (Date.now() - cached.ts > SHARE_CACHE_TTL_MS) {
      _shareLookupCache.delete(cacheKey)
      return undefined
    }
    return cached.vault
  }

  static _writeShareLookupCache(cacheKey, vault) {
    _shareLookupCache.set(cacheKey, {
      ts: Date.now(),
      vault: vault || null
    })
  }

  static async _getAccessibleCampaignIds(viewerUid) {
    const uid = String(viewerUid || '').trim()
    if (!uid) return []

    const cached = _campaignAccessCache.get(uid)
    if (cached && Date.now() - cached.ts <= CAMPAIGN_CACHE_TTL_MS) {
      return cached.ids
    }

    try {
      const campaignsRef = collection(db, 'campaigns')
      const roleFieldPath = new FieldPath('players', uid, 'role')
      const [dmSnap, playerSnap] = await Promise.all([
        getDocs(query(campaignsRef, where('dm', '==', uid))),
        getDocs(query(campaignsRef, where(roleFieldPath, 'in', ['player', 'moderator'])))
      ])

      const ids = Array.from(
        new Set([
          ...(dmSnap?.docs || []).map((docSnap) => String(docSnap.id || '')).filter(Boolean),
          ...(playerSnap?.docs || []).map((docSnap) => String(docSnap.id || '')).filter(Boolean)
        ])
      )

      _campaignAccessCache.set(uid, { ts: Date.now(), ids })
      return ids
    } catch (e) {
      VaultService._log('campaign membership lookup failed', e?.code, e?.message)
      _campaignAccessCache.set(uid, { ts: Date.now(), ids: [] })
      return []
    }
  }

  static async _resolveFromQueries(queries, { slug, viewerUid, accessibleCampaignIds, ownerId }) {
    for (const entry of queries) {
      try {
        const snap = await getDocs(entry.q)
        const docs = Array.isArray(snap?.docs) ? snap.docs : []
        for (const docSnap of docs) {
          const data = docSnap?.data?.() || {}
          if (ownerId && getVaultOwnerId(data) !== ownerId) continue
          if (!vaultMatchesSlug(data, slug)) continue
          if (!canViewerReadVault(data, viewerUid, accessibleCampaignIds)) continue
          VaultService._log(`${entry.label} hit`, docSnap.id)
          return normalizeVault(docSnap)
        }
      } catch (e) {
        VaultService._log(`${entry.label} query failed`, e?.code, e?.message)
      }
    }
    return null
  }

  static async _waitForAuthReady(timeoutMs = 1200, requireUser = false, expectedUid = null) {
    const isMatchingUser = (user) => {
      if (!requireUser) return true
      const uid = user?.uid || null
      return !!uid && (!expectedUid || uid === expectedUid)
    }

    const ensureTokenReady = async (user) => {
      if (!user) return true
      try {
        await getIdTokenResult(user)
      } catch {}
      return true
    }

    try {
      const currentUser = auth?.currentUser || null
      if (isMatchingUser(currentUser)) {
        await ensureTokenReady(currentUser)
        return true
      }
    } catch {}

    return new Promise((resolve) => {
      let settled = false
      let unsub = null
      let timer = null
      const done = (value) => {
        if (settled) return
        settled = true
        try {
          if (typeof unsub === 'function') unsub()
        } catch {}
        if (timer) clearTimeout(timer)
        resolve(!!value)
      }
      try {
        unsub = onIdTokenChanged(auth, async (user) => {
          if (!isMatchingUser(user)) return
          await ensureTokenReady(user)
          done(true)
        })
      } catch {
        resolve(false)
        return
      }
      timer = setTimeout(() => done(false), timeoutMs)
    })
  }
  /**
   * List all vaults accessible to a user
   * @param {Object} ctx - Context with userId, campaignId, etc.
   * @returns {Promise<Array>} Array of vault objects
   */
  static async listVaults(ctx = {}) {
    if (!ctx.userId) return []

    const authReady = await VaultService._waitForAuthReady(5000, !!ctx.userId, ctx.userId)
    if (!authReady) {
      const err = new Error('Authenticated session is not ready yet')
      err.code = 'auth/not-ready'
      throw err
    }

    const vaults = []
    const seen = new Set()
    const colRef = collection(db, COLLECTION)

    // 1) User-owned vaults (always readable)
    try {
      const ownedQuery = query(colRef, where('ownerId', '==', ctx.userId))
      const ownedSnap = await getDocs(ownedQuery)
      ownedSnap.docs.forEach((docSnap) => {
        const v = normalizeVault(docSnap)
        if (v && !seen.has(v.id)) {
          seen.add(v.id)
          vaults.push(v)
        }
      })
    } catch (e) {
      VaultService._log('owned vaults query failed', e?.code, e?.message)
      // Retry once after brief delay in case auth token just attached
      try {
        await new Promise((r) => setTimeout(r, 250))
        const ownedQuery = query(colRef, where('ownerId', '==', ctx.userId))
        const ownedSnap = await getDocs(ownedQuery)
        ownedSnap.docs.forEach((docSnap) => {
          const v = normalizeVault(docSnap)
          if (v && !seen.has(v.id)) {
            seen.add(v.id)
            vaults.push(v)
          }
        })
      } catch {}
    }

    // 2) Campaign-scoped vaults visible to the user (PUBLIC or CAMPAIGN/*)
    //    IMPORTANT: add an access filter to satisfy security rules and avoid permission errors.
    if (ctx.campaignId) {
      try {
        const allowedAccess = ['PUBLIC', 'CAMPAIGN', 'CAMPAIGN_EDIT']
        const campaignQuery = query(
          colRef,
          where('campaignId', '==', ctx.campaignId),
          where('access', 'in', allowedAccess)
        )
        const campaignSnap = await getDocs(campaignQuery)
        campaignSnap.docs.forEach((docSnap) => {
          const v = normalizeVault(docSnap)
          if (v && !seen.has(v.id)) {
            seen.add(v.id)
            vaults.push(v)
          }
        })
      } catch (e) {
        VaultService._log('campaign vaults query failed', e?.code, e?.message)
      }
    }

    // 3) User-specific shared vaults (USER_SPECIFIC + allowedUserIds contains user)
    try {
      const sharedQuery = query(
        colRef,
        where('access', '==', 'USER_SPECIFIC'),
        where('allowedUserIds', 'array-contains', ctx.userId)
      )
      const sharedSnap = await getDocs(sharedQuery)
      sharedSnap.docs.forEach((docSnap) => {
        const v = normalizeVault(docSnap)
        if (v && !seen.has(v.id)) {
          seen.add(v.id)
          vaults.push(v)
        }
      })
    } catch (e) {
      VaultService._log('user-specific vaults query failed', e?.code, e?.message)
      // Retry once after brief delay
      try {
        await new Promise((r) => setTimeout(r, 250))
        const sharedQuery = query(
          colRef,
          where('access', '==', 'USER_SPECIFIC'),
          where('allowedUserIds', 'array-contains', ctx.userId)
        )
        const sharedSnap = await getDocs(sharedQuery)
        sharedSnap.docs.forEach((docSnap) => {
          const v = normalizeVault(docSnap)
          if (v && !seen.has(v.id)) {
            seen.add(v.id)
            vaults.push(v)
          }
        })
      } catch {}
    }

    // Sort by name
    return vaults.sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * Get a single vault by ID
   * @param {string} vaultId - Vault document ID
   * @returns {Promise<Object|null>} Vault object or null
   */
  static async getVault(vaultId) {
    if (!vaultId) return null
    const docRef = doc(db, COLLECTION, vaultId)
    const docSnap = await getDoc(docRef)
    const v = normalizeVault(docSnap)
    // Backfill missing slug for legacy documents
    try {
      if (v && (!v.slug || String(v.slug).trim().length === 0) && v.name) {
        const nextSlug = slugify(String(v.name))
        await updateDoc(docRef, { slug: nextSlug, updatedAt: serverTimestamp() })
        return { ...v, slug: nextSlug }
      }
    } catch {}
    return v
  }

  /**
   * Create a new vault
   * @param {Object} data - Vault data
   * @param {Object} ctx - Context with userId
   * @returns {Promise<Object>} Created vault with ID
   */
  static async createVault(data, ctx = {}) {
    if (!ctx.userId) throw new Error('userId required to create vault')

    const access = String(data.access || 'PRIVATE').toUpperCase()
    const campaignIds = Array.isArray(data.campaignIds)
      ? Array.from(new Set(data.campaignIds.filter(Boolean)))
      : []
    const allowedUserIds = Array.isArray(data.allowedUserIds)
      ? Array.from(new Set(data.allowedUserIds.filter(Boolean)))
      : []
    const name = data.name || VAULT_DEFAULTS.name
    const slug = data.slug ? slugify(String(data.slug)) : slugify(name)

    const payload = {
      ...createVault({ ...data, name }),
      ownerId: ctx.userId,
      name,
      slug,
      access,
      campaignId: data.campaignId || campaignIds[0] || ctx.campaignId || null,
      campaignIds,
      allowedUserIds,
      versionId: data.versionId || ctx.versionId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, COLLECTION), payload)
    return {
      id: docRef.id,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  }

  /**
   * Update a vault
   * @param {string} vaultId - Vault ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated vault data
   */
  static async updateVault(vaultId, updates) {
    if (!vaultId) throw new Error('vaultId required')

    const docRef = doc(db, COLLECTION, vaultId)
    const payload = { ...updates }
    // Normalize access and arrays
    if (payload.access) payload.access = String(payload.access).toUpperCase()
    if (payload.campaignIds)
      payload.campaignIds = Array.from(new Set((payload.campaignIds || []).filter(Boolean)))
    if (payload.allowedUserIds)
      payload.allowedUserIds = Array.from(new Set((payload.allowedUserIds || []).filter(Boolean)))
    // Maintain primary campaignId from campaignIds when not explicitly provided
    if (!payload.campaignId && Array.isArray(payload.campaignIds) && payload.campaignIds.length)
      payload.campaignId = payload.campaignIds[0]
    // Slug follows name if name changes and slug not explicitly set
    if (
      Object.prototype.hasOwnProperty.call(payload, 'name') &&
      !Object.prototype.hasOwnProperty.call(payload, 'slug')
    ) {
      const nextName = String(payload.name || '').trim()
      if (nextName) payload.slug = slugify(nextName)
    }
    payload.updatedAt = serverTimestamp()

    // Don't allow changing ownerId
    delete payload.ownerId
    delete payload.id
    delete payload.createdAt

    await updateDoc(docRef, payload)
    return { id: vaultId, ...updates }
  }

  /**
   * Find a shareable vault by owner and slug honoring access rules (PUBLIC, CAMPAIGN, USER_SPECIFIC)
   */
  static async findShareableVaultByOwnerAndSlug(ownerId, slug) {
    if (!ownerId || !slug) return null
    const normalizedOwnerId = String(ownerId).trim()
    const normalizedSlug = slugify(String(slug))
    if (!normalizedOwnerId || !normalizedSlug) return null
    const colRef = collection(db, COLLECTION)

    // Give auth a moment to initialize so owners/campaign members don't get treated as anonymous
    try {
      if (!auth?.currentUser) {
        await VaultService._waitForAuthReady(3000)
      }
    } catch {}
    const viewerUid = auth.currentUser?.uid || null
    const cacheKey = `${normalizedOwnerId}|${normalizedSlug}|${viewerUid || 'anon'}`
    const cached = VaultService._readShareLookupCache(cacheKey)
    if (cached !== undefined) {
      return cached
    }

    const accessibleCampaignIds =
      viewerUid && viewerUid !== normalizedOwnerId
        ? await VaultService._getAccessibleCampaignIds(viewerUid)
        : []
    const accessibleCampaignIdSet = new Set(accessibleCampaignIds.map((value) => String(value)))
    const ownerFields = [
      { field: 'ownerId', value: normalizedOwnerId },
      { field: 'owner', value: normalizedOwnerId }
    ]

    const exactQueries = []
    const scanQueries = []
    const pushExact = (label, q) => exactQueries.push({ label, q })
    const pushScan = (label, q) => scanQueries.push({ label, q })

    VaultService._log('lookup start', {
      ownerId: normalizedOwnerId,
      slug: normalizedSlug,
      viewerUid
    })

    if (!viewerUid) {
      ownerFields.forEach(({ field, value }) => {
        pushExact(
          `public exact ${field}`,
          query(
            colRef,
            where(field, '==', value),
            where('slug', '==', normalizedSlug),
            where('access', '==', 'PUBLIC'),
            limit(1)
          )
        )
        pushScan(
          `public scan ${field}`,
          query(colRef, where(field, '==', value), where('access', '==', 'PUBLIC'), limit(SHARE_SCAN_LIMIT))
        )
      })
    } else if (viewerUid === normalizedOwnerId) {
      ownerFields.forEach(({ field, value }) => {
        pushExact(
          `owner exact ${field}`,
          query(colRef, where(field, '==', value), where('slug', '==', normalizedSlug), limit(1))
        )
        pushScan(
          `owner scan ${field}`,
          query(colRef, where(field, '==', value), limit(SHARE_SCAN_LIMIT))
        )
      })
    } else {
      ownerFields.forEach(({ field, value }) => {
        pushExact(
          `public exact ${field}`,
          query(
            colRef,
            where(field, '==', value),
            where('slug', '==', normalizedSlug),
            where('access', '==', 'PUBLIC'),
            limit(1)
          )
        )
        pushExact(
          `user-specific exact ${field}`,
          query(
            colRef,
            where(field, '==', value),
            where('slug', '==', normalizedSlug),
            where('access', '==', 'USER_SPECIFIC'),
            where('allowedUserIds', 'array-contains', viewerUid),
            limit(1)
          )
        )
        pushScan(
          `public scan ${field}`,
          query(colRef, where(field, '==', value), where('access', '==', 'PUBLIC'), limit(SHARE_SCAN_LIMIT))
        )
        pushScan(
          `user-specific scan ${field}`,
          query(
            colRef,
            where(field, '==', value),
            where('access', '==', 'USER_SPECIFIC'),
            where('allowedUserIds', 'array-contains', viewerUid),
            limit(SHARE_SCAN_LIMIT)
          )
        )

        if (accessibleCampaignIds.length) {
          for (const access of ['CAMPAIGN', 'CAMPAIGN_EDIT']) {
            for (const campaignChunk of chunkArray(accessibleCampaignIds)) {
              pushExact(
                `${access.toLowerCase()} exact ${field}`,
                query(
                  colRef,
                  where(field, '==', value),
                  where('slug', '==', normalizedSlug),
                  where('access', '==', access),
                  where('campaignId', 'in', campaignChunk),
                  limit(1)
                )
              )
              pushScan(
                `${access.toLowerCase()} scan ${field}`,
                query(
                  colRef,
                  where(field, '==', value),
                  where('access', '==', access),
                  where('campaignId', 'in', campaignChunk),
                  limit(SHARE_SCAN_LIMIT)
                )
              )
            }
          }
        }
      })
    }

    let match = await VaultService._resolveFromQueries(exactQueries, {
      slug: normalizedSlug,
      viewerUid,
      accessibleCampaignIds: accessibleCampaignIdSet,
      ownerId: normalizedOwnerId
    })

    if (!match) {
      match = await VaultService._resolveFromQueries(scanQueries, {
        slug: normalizedSlug,
        viewerUid,
        accessibleCampaignIds: accessibleCampaignIdSet,
        ownerId: normalizedOwnerId
      })
    }

    // If viewer is owner and legacy fields are missing, backfill them for future lookups.
    if (match && viewerUid && viewerUid === normalizedOwnerId) {
      try {
        const patch = { updatedAt: serverTimestamp() }
        if (!match.slug) patch.slug = normalizedSlug
        if (!match.ownerId) patch.ownerId = viewerUid
        if (Object.keys(patch).length > 1) {
          await updateDoc(doc(db, COLLECTION, match.id), patch)
        }
      } catch {}
    }

    VaultService._writeShareLookupCache(cacheKey, match)
    if (!match) {
      VaultService._log('no match found for', {
        ownerId: normalizedOwnerId,
        slug: normalizedSlug,
        viewerUid
      })
    }
    return match
  }

  /**
   * Delete a vault and all its notes
   * @param {string} vaultId - Vault ID
   * @returns {Promise<boolean>} Success
   */
  static async deleteVault(vaultId) {
    if (!vaultId) throw new Error('vaultId required')

    const batch = writeBatch(db)

    // Delete all notes in the vault
    const notesRef = collection(db, COLLECTION, vaultId, 'notes')
    const notesSnap = await getDocs(notesRef)
    notesSnap.docs.forEach((noteDoc) => {
      batch.delete(noteDoc.ref)
    })

    // Delete the vault document
    batch.delete(doc(db, COLLECTION, vaultId))

    await batch.commit()
    return true
  }

  // ============================================================================
  // FOLDER METADATA (stored within vault document)
  // ============================================================================

  /**
   * Get folder metadata for a vault
   * @param {string} vaultId - Vault ID
   * @returns {Promise<Object>} Folder metadata map
   */
  static async getFolderMeta(vaultId) {
    const vault = await VaultService.getVault(vaultId)
    return vault?.folderMeta || {}
  }

  /**
   * Update folder metadata for a specific folder
   * @param {string} vaultId - Vault ID
   * @param {string} folderPath - Folder path
   * @param {Object} meta - Metadata to set { icon, ... }
   */
  static async updateFolderMeta(vaultId, folderPath, meta) {
    if (!vaultId || !folderPath) return

    const vault = await VaultService.getVault(vaultId)
    if (!vault) throw new Error('Vault not found')

    const folderMeta = { ...vault.folderMeta }

    if (meta && Object.keys(meta).length > 0) {
      folderMeta[folderPath] = { ...folderMeta[folderPath], ...meta }
    } else {
      delete folderMeta[folderPath]
    }

    await VaultService.updateVault(vaultId, { folderMeta })
    return folderMeta[folderPath]
  }

  /**
   * Set folder icon
   * @param {string} vaultId - Vault ID
   * @param {string} folderPath - Folder path
   * @param {string|null} icon - Icon string or null to remove
   */
  static async setFolderIcon(vaultId, folderPath, icon) {
    return VaultService.updateFolderMeta(vaultId, folderPath, icon ? { icon } : null)
  }

  /**
   * Rename folder in metadata
   * @param {string} vaultId - Vault ID
   * @param {string} oldPath - Old folder path
   * @param {string} newPath - New folder path
   */
  static async renameFolderMeta(vaultId, oldPath, newPath) {
    if (!vaultId || !oldPath || oldPath === newPath) return

    const vault = await VaultService.getVault(vaultId)
    if (!vault) return

    const folderMeta = { ...vault.folderMeta }
    const updated = {}

    for (const [path, meta] of Object.entries(folderMeta)) {
      if (path === oldPath) {
        updated[newPath] = meta
      } else if (path.startsWith(oldPath + '/')) {
        const newChildPath = newPath + path.slice(oldPath.length)
        updated[newChildPath] = meta
      } else {
        updated[path] = meta
      }
    }

    await VaultService.updateVault(vaultId, { folderMeta: updated })
  }

  /**
   * Delete folder metadata
   * @param {string} vaultId - Vault ID
   * @param {string} folderPath - Folder path to delete
   */
  static async deleteFolderMeta(vaultId, folderPath) {
    if (!vaultId || !folderPath) return

    const vault = await VaultService.getVault(vaultId)
    if (!vault) return

    const folderMeta = { ...vault.folderMeta }
    const updated = {}

    for (const [path, meta] of Object.entries(folderMeta)) {
      if (path !== folderPath && !path.startsWith(folderPath + '/')) {
        updated[path] = meta
      }
    }

    await VaultService.updateVault(vaultId, { folderMeta: updated })
  }

  // ============================================================================
  // VAULT SETTINGS
  // ============================================================================

  /**
   * Get vault settings
   * @param {string} vaultId - Vault ID
   * @returns {Promise<Object>} Settings object
   */
  static async getSettings(vaultId) {
    const vault = await VaultService.getVault(vaultId)
    return { ...VAULT_DEFAULTS.settings, ...(vault?.settings || {}) }
  }

  /**
   * Update vault settings
   * @param {string} vaultId - Vault ID
   * @param {Object} settings - Settings to update
   */
  static async updateSettings(vaultId, settings) {
    const vault = await VaultService.getVault(vaultId)
    if (!vault) throw new Error('Vault not found')

    const merged = { ...vault.settings, ...settings }
    await VaultService.updateVault(vaultId, { settings: merged })
    return merged
  }

  // ============================================================================
  // DEFAULT VAULT HELPERS
  // ============================================================================

  /**
   * Get or create default vault for a user
   * Creates a vault if the user has none
   * @param {Object} ctx - Context with userId
   * @returns {Promise<Object>} Vault object
   */
  static async getOrCreateDefaultVault(ctx) {
    if (!ctx.userId) throw new Error('userId required')

    const vaults = await VaultService.listVaults(ctx)

    // Return first vault if exists
    if (vaults.length > 0) {
      // Prefer a vault matching current campaign if specified
      if (ctx.campaignId) {
        const campaignVault = vaults.find((v) => v.campaignId === ctx.campaignId)
        if (campaignVault) return campaignVault
      }
      return vaults[0]
    }

    // Create default vault
    return VaultService.createVault(
      {
        name: ctx.campaignId ? 'Campaign Notes' : 'My Notes',
        campaignId: ctx.campaignId || null
      },
      ctx
    )
  }
}

export default VaultService
